// /audit — super-admin-only audit timeline. Lists every recorded
// mutation across the system: who did what, when, against which
// resource. Filters: by user_id (free-text input matched on email →
// user_id resolution server-side via /api/audit ?user_id=…) and
// action prefix (e.g. 'source.', 'secret.', 'auth.').

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getMe, listAudit } from '../api';
import type { AuditEntryClient } from '../api';
import { Table } from '../components/Table';
import type { Column } from '../components/Table';
import TopHeader from '../components/TopHeader';
import type { UserPublic } from '@datahub/shared';

const ACTION_GROUPS: Array<{ label: string; prefix: string }> = [
  { label: 'All', prefix: '' },
  { label: 'Auth', prefix: 'auth.' },
  { label: 'Sources', prefix: 'source.' },
  { label: 'Triggers', prefix: 'trigger.' },
  { label: 'Secrets', prefix: 'secret.' },
  { label: 'Views', prefix: 'view.' },
  { label: 'BQ Views', prefix: 'bq_view.' },
  { label: 'Replays', prefix: 'replay.' },
  { label: 'Schedules', prefix: 'schedule.' },
  { label: 'Workspaces', prefix: 'workspace.' },
  { label: 'API Keys', prefix: 'api_key.' },
];

export default function AuditPage(): React.ReactElement {
  const [me, setMe] = useState<UserPublic | null | undefined>(undefined);
  const [entries, setEntries] = useState<AuditEntryClient[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [actionPrefix, setActionPrefix] = useState('');
  const [userIdFilter, setUserIdFilter] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => { getMe().then(setMe).catch(() => setMe(null)); }, []);

  async function reload(): Promise<void> {
    setLoading(true); setError(null);
    try {
      const rows = await listAudit({
        limit: 200,
        actionPrefix: actionPrefix || undefined,
        userId: userIdFilter || undefined,
      });
      setEntries(rows);
    } catch (e) {
      setError(String(e));
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { if (me?.is_super_admin) reload(); }, [me?.is_super_admin, actionPrefix, userIdFilter]);

  if (me === undefined) return <div style={{ minHeight: '100vh' }} />;
  if (!me) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <p style={{ color: 'var(--ink-2)' }}>You need to sign in to view this page.</p>
        <Link to="/" style={{ color: 'var(--ink)' }}>← Home</Link>
      </div>
    );
  }
  if (!me.is_super_admin) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <p style={{ color: 'var(--ink-2)' }}>The audit log is super-admin only.</p>
        <Link to="/" style={{ color: 'var(--ink)' }}>← Home</Link>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', color: 'var(--ink)' }}>
      <TopHeader env="Audit" hideLive />

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 24px 64px' }}>
        <h1 style={{ fontSize: 22, fontWeight: 600, margin: '0 0 6px' }}>Audit log</h1>
        <p style={{ fontSize: 12.5, color: 'var(--ink-2)', margin: '0 0 18px' }}>
          Append-only record of every operator action. Last 200 entries shown; filter by category or user.
        </p>

        {/* Filters */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
          {ACTION_GROUPS.map(g => (
            <button
              key={g.prefix || 'all'}
              onClick={() => setActionPrefix(g.prefix)}
              style={{
                padding: '4px 10px', fontSize: 11.5,
                border: '1px solid', borderColor: actionPrefix === g.prefix ? 'var(--ink)' : 'var(--line)',
                background: actionPrefix === g.prefix ? 'var(--ink)' : 'var(--bg)',
                color: actionPrefix === g.prefix ? 'var(--bg)' : 'var(--ink)',
                borderRadius: 4, cursor: 'pointer',
              }}
            >{g.label}</button>
          ))}
          <input
            value={userIdFilter}
            onChange={e => setUserIdFilter(e.target.value)}
            placeholder="filter by user_id (usr_…)"
            style={{ flex: '1 1 240px', padding: '4px 10px', fontSize: 11.5, fontFamily: 'var(--mono)', border: '1px solid var(--line)', borderRadius: 4, background: 'var(--bg)' }} />
          <button onClick={reload} className="btn sm ghost" style={{ fontSize: 11.5 }}>{loading ? '…' : 'Refresh'}</button>
        </div>

        {error && <div style={{ padding: 12, background: 'oklch(0.95 0.05 30)', color: 'oklch(0.32 0.07 28)', borderRadius: 6, marginBottom: 16, fontSize: 12.5 }}>{error}</div>}

        <Table<AuditEntryClient>
          columns={auditColumns}
          rows={entries}
          getRowKey={e => e.audit_id}
          dense
          stickyHeader
          emptyState={
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink-3)', fontSize: 13, border: '1px dashed var(--line)', borderRadius: 10 }}>
              {loading ? 'Loading…' : 'No entries match the current filter.'}
            </div>
          }
        />
      </div>
    </div>
  );
}

const auditColumns: Column<AuditEntryClient>[] = [
  {
    id: 'when',
    label: 'When',
    width: 160,
    render: e => <span style={{ whiteSpace: 'nowrap', color: 'var(--ink-2)' }}>{new Date(e.created_at).toLocaleString()}</span>,
  },
  {
    id: 'user',
    label: 'User',
    width: 220,
    render: e => (
      <div>
        <div>{e.user_email}</div>
        <div style={{ fontSize: 10, color: 'var(--ink-3)', fontFamily: 'var(--mono)' }}>{e.user_id}</div>
      </div>
    ),
  },
  {
    id: 'action',
    label: 'Action',
    width: 180,
    mono: true,
    render: e => <span style={{ fontWeight: 500 }}>{e.action}</span>,
  },
  {
    id: 'resource',
    label: 'Resource',
    width: 240,
    mono: true,
    render: e => (
      <span>
        {e.resource_kind && <span style={{ color: 'var(--ink-3)' }}>{e.resource_kind}: </span>}
        <span style={{ color: 'var(--ink-2)' }}>{e.resource_id ?? '—'}</span>
      </span>
    ),
  },
  {
    id: 'detail',
    label: 'Detail',
    mono: true,
    ellipsis: true,
    render: e => <span style={{ color: 'var(--ink-2)' }}>{e.detail ? JSON.stringify(e.detail) : '—'}</span>,
  },
  {
    id: 'ip',
    label: 'IP',
    width: 120,
    mono: true,
    render: e => <span style={{ color: 'var(--ink-3)' }}>{e.ip}</span>,
  },
];
